/** Page-only subscription builder. Product data is emitted once by Liquid and kept in memory. */
class SubscriptionSection {
  constructor(section) {
    this.section = section;
    this.products = JSON.parse(section.querySelector('[data-subscription-products]').textContent);
    this.forms = [...section.querySelectorAll('[data-subscription-form]')];
    this.activeKey = section.dataset.activeProduct;
    this.storageKey = `subscription-builder-properties-${section.id}`;
    this.bind();
    this.restoreProperties();
    this.switchProduct(this.activeKey, false);
  }

  bind() {
    this.section.addEventListener('click', (event) => {
      const plan = event.target.closest('[data-subscription-plan]');
      if (plan) this.switchProduct(plan.dataset.subscriptionPlan);
    });
    this.section.addEventListener('keydown', (event) => {
      const plan = event.target.closest('[data-subscription-plan]');
      if (!plan || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const plans = [...this.section.querySelectorAll('[data-subscription-plan]')];
      const current = plans.indexOf(plan);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? plans.length - 1 : (current + (event.key === 'ArrowRight' ? 1 : -1) + plans.length) % plans.length;
      event.preventDefault(); this.switchProduct(plans[next].dataset.subscriptionPlan);
    });
    this.section.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-input]')) {
        const field = event.target.closest('[data-subscription-field]');
        if (field) {
          const customInput = field.querySelector('[data-custom-hear-input]');
          if (customInput) { const custom = event.target.value === 'Other'; customInput.classList.toggle('is-visible', custom); customInput.required = custom; if (!custom) customInput.value = ''; }
          this.clearFieldError(field);
        }
        this.saveProperties();
        const form = event.target.closest('[data-subscription-form]');
        if (form) this.updatePriceDisplay(form);
      }
      if (event.target.matches('[data-protection-toggle]')) {
        const text = event.target.closest('.subscription-builder__protection')?.querySelector('[data-protection-text]');
        if (text) text.textContent = event.target.checked ? text.dataset.onText : text.dataset.offText;
      }
    });
    this.section.addEventListener('input', (event) => { if (event.target.matches('[data-custom-hear-input]')) this.saveProperties(); });
    this.section.addEventListener('submit', (event) => {
      if (event.target.matches('[data-subscription-form]')) { event.preventDefault(); this.addToCart(event.target); }
    });
  }

  switchProduct(key, focus = true) {
    this.activeKey = key;
    this.section.dataset.activeProduct = key;
    this.section.querySelectorAll('[data-subscription-plan]').forEach((button) => {
      const active = button.dataset.subscriptionPlan === key;
      button.setAttribute('aria-selected', active);
      button.tabIndex = active ? 0 : -1;
      if (active && focus) button.focus();
    });
    this.forms.forEach((form) => { form.hidden = form.dataset.subscriptionForm !== key; });
  }

  /* Keep only line-item properties for this browser session; variant choices remain form state. */
  saveProperties() {
    const saved = {};
    this.forms.forEach((form) => {
      saved[form.dataset.subscriptionForm] = {};
      form.querySelectorAll('[data-option-input][data-property]:checked').forEach((input) => { saved[form.dataset.subscriptionForm][input.dataset.property] = input.value; });
      const custom = form.querySelector('[data-custom-hear-input]');
      if (custom?.value) saved[form.dataset.subscriptionForm].customHear = custom.value;
    });
    try { sessionStorage.setItem(this.storageKey, JSON.stringify(saved)); } catch (_) { /* Storage can be unavailable in private browsing. */ }
  }

  restoreProperties() {
    let saved;
    try { saved = JSON.parse(sessionStorage.getItem(this.storageKey) || '{}'); } catch (_) { return; }
    this.forms.forEach((form) => {
      const properties = saved[form.dataset.subscriptionForm] || {};
      Object.entries(properties).forEach(([name, value]) => {
        if (name === 'customHear') return;
        const input = [...form.querySelectorAll('[data-option-input][data-property]')].find((item) => item.dataset.property === name && item.value === value);
        if (input) { input.checked = true; input.dispatchEvent(new Event('change', { bubbles: true })); }
      });
      const custom = form.querySelector('[data-custom-hear-input]');
      if (custom && properties.customHear) { custom.value = properties.customHear; const other = [...form.querySelectorAll('[data-option-input][data-property]')].find((item) => item.value === 'Other'); if (other) { other.checked = true; custom.classList.add('is-visible'); custom.required = true; } }
    });
  }

  validate(form) {
    const fields = [...form.querySelectorAll('[data-subscription-field]')];
    let firstInvalid;
    fields.forEach((field) => {
      const checked = field.querySelector('input:checked');
      const custom = field.querySelector('[data-custom-hear-input]');
      if (!checked || (custom?.required && !custom.value.trim())) { this.showFieldError(field, custom?.required ? 'Please tell us how you heard about us.' : 'Please choose an option.'); firstInvalid ||= field; } else this.clearFieldError(field);
    });
    if (firstInvalid) { firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' }); const customInput = firstInvalid.querySelector('[data-custom-hear-input]'); (customInput?.required ? customInput : firstInvalid.querySelector('input')).focus(); return false; }
    return true;
  }

  showFieldError(field, message) { field.classList.add('is-invalid'); field.querySelector('.subscription-builder__error').textContent = message; }
  clearFieldError(field) { field?.classList.remove('is-invalid'); }

  selectedVariant(form, product) {
    if (!product || !Array.isArray(product.variants)) return null;
    const seenNames = new Set();
    const choices = [];
    form.querySelectorAll('[data-option-input][data-variant-option]').forEach((input) => {
      if (seenNames.has(input.name)) return;
      seenNames.add(input.name);
      if (input.type === 'checkbox') { choices.push(input.checked ? input.value : input.dataset.uncheckedValue); return; }
      const checkedInGroup = form.querySelector(`[name="${CSS.escape(input.name)}"]:checked`);
      if (checkedInGroup) choices.push(checkedInGroup.value);
    });
    const dimensions = product.variants[0]?.options?.length || 0;
    if (!choices.length || choices.length < dimensions) return null;
    const matchesChoices = (variant) => Array.isArray(variant.options) && choices.every((value, index) => String(variant.options[index]) === String(value));
    const selected = product.variants.find((variant) => variant.available && matchesChoices(variant)) || product.variants.find(matchesChoices);
    console.log('[Subscription Builder] Variant resolution', { choices, variants: product.variants, selectedVariant: selected || null });
    return selected;
  }

  updatePriceDisplay(form) {
    const priceEl = form.querySelector('[data-price-value]');
    if (!priceEl) return;
    const product = this.products[form.dataset.subscriptionForm];
    const variant = this.selectedVariant(form, product);
    const captionEl = form.querySelector('[data-price-caption]');
    if (variant?.price) {
      priceEl.textContent = variant.price;
      if (captionEl) captionEl.textContent = 'Total';
    } else {
      priceEl.textContent = priceEl.dataset.basePrice;
      if (captionEl) captionEl.textContent = 'From';
    }
  }

  collectProperties(form) {
    const properties = {};
    form.querySelectorAll('[data-option-input]:checked').forEach((input) => { if (!input.dataset.variantOption) { const custom = input.closest('[data-subscription-field]').querySelector('[data-custom-hear-input]'); properties[input.dataset.property] = input.value === 'Other' && custom ? custom.value.trim() : input.value; } });
    return properties;
  }

  async addToCart(form) {
    if (!this.validate(form)) return;
    const product = this.products[this.activeKey];
    const variant = this.selectedVariant(form, product);
    const status = form.querySelector('[data-subscription-status]');
    console.log('[Subscription Builder] Add to cart submitted', { plan: this.activeKey, product, variant, properties: this.collectProperties(form) });
    if (!product?.sellingPlanId) { status.textContent = 'This subscription product has no selling plan configured.'; status.classList.add('is-error'); return; }
    if (!variant) { status.textContent = 'This combination is unavailable. Please choose another option.'; status.classList.add('is-error'); return; }
    const submit = form.querySelector('[type="submit"]');
    submit.disabled = true; status.classList.remove('is-error'); status.textContent = 'Adding to cart…';
    try {
      const bundledSections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: bundledSections } }));
      const request = new FormData(); request.append('id', variant.id); request.append('quantity', '1'); request.append('selling_plan', product.sellingPlanId); request.append('sections', bundledSections.join(',')); request.append('sections_url', window.location.pathname);
      Object.entries(this.collectProperties(form)).forEach(([key, value]) => request.append(`properties[${key}]`, value));
      const config = window.theme?.utils?.fetchConfig ? window.theme.utils.fetchConfig('javascript') : { method: 'POST', headers: { Accept: 'application/json' } };
      config.headers['X-Requested-With'] = 'XMLHttpRequest'; delete config.headers['Content-Type']; config.body = request;
      const response = await fetch(window.theme?.routes?.cart_add_url || '/cart/add.js', config);
      if (!response.ok) throw new Error((await response.json()).description || 'Unable to add this plan to your cart.');
      const added = await response.json();
      const cart = await fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart.js` : '/cart.js').then((r) => r.json());
      cart.sections = added.sections;
      if (window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'subscription-builder', productVariantId: variant.id, cart });
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
      const drawer = this.section.dataset.enableCartDrawer === 'true' && document.querySelector('cart-drawer');
      if (drawer?.show) { drawer.show(submit); status.textContent = 'Added to cart.'; } else window.location.assign(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart` : '/cart');
    } catch (error) { status.textContent = error.message || 'Unable to add this plan to your cart.'; status.classList.add('is-error'); submit.disabled = false; }
  }
}
const initSubscriptionSections = (root = document) => root.querySelectorAll('[data-subscription-builder]').forEach((section) => { if (!section.subscriptionBuilder) section.subscriptionBuilder = new SubscriptionSection(section); });
document.addEventListener('DOMContentLoaded', () => initSubscriptionSections());
document.addEventListener('shopify:section:load', (event) => initSubscriptionSections(event.target));
